/* eslint-disable no-console -- Warnings for rejected impersonation attempts are intentional */
import { computed, Injectable, signal } from '@angular/core';
import { UserProfileDtoV2 } from 'api-client';

import { ImpersonatedUserInfo, ImpersonationState } from '../models/impersonation.model';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

@Injectable({ providedIn: 'root' })
export class ImpersonationStore {
  private readonly STORAGE_KEY = 'dreampay_impersonation_state';
  // Known role ID for Customer
  private readonly CUSTOMER_ROLE_ID = '5525A30C-907F-4BB7-A354-A2C9FB1F32AA';
  // Known role name for SuperAdmin
  private readonly SUPERADMIN_ROLE_NAME = 'SuperAdmin';
  // Known role name for Admin
  private readonly ADMIN_ROLE_NAME = 'Admin';

  constructor() {
    this.restoreFromStorage();
  }

  private readonly _state = signal<ImpersonationState>({
    isActive: false,
    impersonatedUser: null,
    originalUser: null,
  });

  readonly state = this._state.asReadonly();
  readonly isImpersonating = computed(() => this._state().isActive);
  readonly impersonatedUser = computed(() => this._state().impersonatedUser);
  readonly impersonatedUserName = computed(() => this._state().impersonatedUser?.name ?? null);
  readonly originalUser = computed(() => this._state().originalUser);
  readonly impersonatedRole = computed(() => this._state().impersonatedUser?.role ?? null);
  readonly impersonatedPermissions = computed(
    () => this._state().impersonatedUser?.permissions ?? [],
  );

  private saveToStorage(state: ImpersonationState): void {
    if (!isBrowser()) return;
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private restoreFromStorage(): void {
    if (!isBrowser()) return;

    const stored = sessionStorage.getItem(this.STORAGE_KEY);

    if (!stored) return;

    try {
      const data = JSON.parse(stored) as unknown;
      if (this.isValidState(data)) {
        this._state.set(data);
      } else {
        sessionStorage.removeItem(this.STORAGE_KEY);
      }
    } catch {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  clearStorage(): void {
    if (!isBrowser()) return;
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  private isValidState(data: unknown): data is ImpersonationState {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    if (typeof obj['isActive'] !== 'boolean') return false;
    // If isActive is false, impersonatedUser and originalUser can be null
    if (!obj['isActive']) return true;
    // If isActive is true, both users must be valid
    return (
      this.isValidUserInfo(obj['impersonatedUser']) && this.isValidUserInfo(obj['originalUser'])
    );
  }

  private isValidUserInfo(data: unknown): data is ImpersonatedUserInfo {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    const hasBaseFields =
      typeof obj['id'] === 'string' &&
      typeof obj['name'] === 'string' &&
      typeof obj['email'] === 'string';
    if (!hasBaseFields) return false;
    // Backwards-compat: fill missing role/permissions from older stored state
    if (!('role' in obj)) (obj as Record<string, unknown>)['role'] = '';
    if (!('permissions' in obj)) (obj as Record<string, unknown>)['permissions'] = [];
    return true;
  }

  /**
   * Check if a user can be impersonated (must have Customer or Admin role)
   */
  canBeImpersonated(user: UserProfileDtoV2): boolean {
    if (!user.roles || user.roles.length === 0) return false;
    const hasCustomerRole = user.roles.some(
      (role) => role.roleId?.toUpperCase() === this.CUSTOMER_ROLE_ID,
    );
    const hasAdminRole = user.roles.some(
      (role) => role.name?.toLowerCase() === this.ADMIN_ROLE_NAME.toLowerCase(),
    );
    return hasCustomerRole || hasAdminRole;
  }

  /**
   * Check if a user can impersonate others (must have SuperAdmin role)
   */
  canImpersonate(user: UserProfileDtoV2 | null): boolean {
    if (!user || !user.roles || user.roles.length === 0) return false;
    return user.roles.some(
      (role) => role.name?.toLowerCase() === this.SUPERADMIN_ROLE_NAME.toLowerCase(),
    );
  }

  /**
   * Start impersonating a user
   * @param targetUser - User to impersonate (must have Customer or Admin role)
   * @param currentUser - Current user (must be SuperAdmin)
   * @param role - Role name of the impersonated user (e.g. 'Admin')
   * @param permissions - Permission names for the impersonated user's role
   */
  startImpersonation(
    targetUser: UserProfileDtoV2,
    currentUser: UserProfileDtoV2,
    role = '',
    permissions: string[] = [],
  ): void {
    if (!isBrowser()) return;

    // Validate target can be impersonated
    if (!this.canBeImpersonated(targetUser)) {
      console.warn('Target user cannot be impersonated (no Customer/Admin role)');
      return;
    }

    // Validate current user can impersonate
    if (!this.canImpersonate(currentUser)) {
      console.warn('Current user cannot impersonate (no SuperUser role)');
      return;
    }

    const newState: ImpersonationState = {
      isActive: true,
      impersonatedUser: {
        id: targetUser.id ?? '',
        name: `${targetUser.firstName} ${targetUser.lastName}`.trim(),
        email: targetUser.email,
        role,
        permissions,
      },
      originalUser: {
        id: currentUser.id ?? '',
        name: `${currentUser.firstName} ${currentUser.lastName}`.trim(),
        email: currentUser.email,
        role: '',
        permissions: [],
      },
    };

    this._state.set(newState);
    this.saveToStorage(newState);

    // Clear cached establishment to force fresh load for impersonated user
    localStorage.removeItem('currentEstablishment');

    // Full page reload to reset app context (per user context decision)
    window.location.reload();
  }

  /**
   * End impersonation and restore original user context
   * No confirmation needed (per user context - quick escape)
   * Always navigates to dashboard after exiting
   */
  endImpersonation(): void {
    if (!isBrowser()) return;

    const defaultState: ImpersonationState = {
      isActive: false,
      impersonatedUser: null,
      originalUser: null,
    };

    this._state.set(defaultState);
    this.clearStorage();

    // Clear cached establishment to force fresh load for original user
    localStorage.removeItem('currentEstablishment');

    // Navigate to dashboard after exiting impersonation
    // Using window.location.href instead of reload to ensure consistent landing page
    window.location.href = '/dashboard';
  }
}
